import * as React from 'react';
import { ChangeEvent, DragEvent, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Uploader } from '../../components/Uploader';
import { uploadTemporaryAdditionalFile } from '../../actions/form/uploadTemporaryFile';
import { removeFileByName, setErrors } from '../../actions/form';
import { TThunkDispatch } from '../../types/redux';
import { selectForm } from '../../selectors/form';
import { validateMimeType } from '../Uploader/utils/validateMimeType';
import { validateSize } from '../Uploader/utils/validateSize';

const FILE_SIZE_ERROR = { additionalFiles: 'Размер файла не должен превышать 10МБ' };
const MAX_FILES = 2;

interface IMultiUploaderContainerProps {
  uploaderText?: string;
}

export const MultiUploaderContainer: React.FC<IMultiUploaderContainerProps> = ({ uploaderText }) => {
  const [files, setFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [dragDisabled, setDragDisabled] = useState(false);
  const dispatch = useDispatch<TThunkDispatch>();
  const {
    errors: { additionalFiles: error },
    isFileUploading,
    isLoading,
  } = useSelector(selectForm);

  const handleDrag = (e: DragEvent<HTMLInputElement>) => {
    e.preventDefault();

    if (files.length >= MAX_FILES) {
      setDragDisabled(true);

      return;
    }

    const items = e.dataTransfer.items;

    if (!items?.length || !validateMimeType(items[0].type)) {
      setDragDisabled(true);

      return;
    }

    setDragActive(true);
  };

  const handleLeave = (e: DragEvent<HTMLInputElement>) => {
    e.preventDefault();
    setDragActive(false);
    setDragDisabled(false);
  };

  const uploadAndSet = async (newFiles: FileList) => {
    const filesToAdd: File[] = [];

    for (let i = 0; i < newFiles.length && files.length + filesToAdd.length < MAX_FILES; i++) {
      const file = newFiles[i];

      if (!validateMimeType(file.type)) {
        continue;
      }

      if (!validateSize(file)) {
        dispatch(setErrors(FILE_SIZE_ERROR));
        continue;
      }

      filesToAdd.push(file);
    }

    if (filesToAdd.length === 0) {
      return;
    }

    dispatch(setErrors({}));

    for (const file of filesToAdd) {
      if (!(await dispatch(uploadTemporaryAdditionalFile(file)))) {
        continue;
      }
    }

    setFiles(prev => [...prev, ...filesToAdd]);
  };

  const handleDrop = async (e: DragEvent<HTMLInputElement>) => {
    e.preventDefault();
    setDragActive(false);
    setDragDisabled(false);

    const droppedFiles = e.dataTransfer.files;

    if (!droppedFiles?.length) {
      return;
    }

    await uploadAndSet(droppedFiles);
  };

  const handleChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.currentTarget.files;

    if (!selectedFiles?.length) {
      return;
    }

    await uploadAndSet(selectedFiles);
  };

  const clearFile = (e: React.MouseEvent, filename: string) => {
    e.preventDefault();
    setFiles(prev => prev.filter(file => file.name !== filename));

    dispatch(removeFileByName(filename));
  };

  return (
    <>
      {files.map((file, index) => (
        <Uploader
          key={`${file.name}-${index}`}
          file={file}
          hasError={!!error}
          isFileUploading={isFileUploading}
          dragActive={false}
          dragDisabled={false}
          isDisabled={isLoading}
          handleDrag={() => {}}
          handleLeave={() => {}}
          handleDrop={() => {}}
          handleChange={() => {}}
          handleClickCross={event => clearFile(event, file.name)}
          isMultiple={true}
          uploaderText={uploaderText}
        />
      ))}

      {files.length < MAX_FILES && (
        <Uploader
          file={null}
          hasError={!!error}
          isFileUploading={isFileUploading}
          dragActive={dragActive}
          dragDisabled={dragDisabled || files.length >= MAX_FILES}
          isDisabled={isLoading}
          handleDrag={handleDrag}
          handleLeave={handleLeave}
          handleDrop={handleDrop}
          handleChange={handleChange}
          handleClickCross={() => {}}
          isMultiple={true}
          uploaderText={uploaderText}
        />
      )}
    </>
  );
};

MultiUploaderContainer.displayName = 'MultiUploaderContainer';
